import Ajax from './ajax';
import { toast } from 'react-toastify';
import i18next from 'i18next';
import { LOGGED_OFF } from './authentication';

export const SURVEY_SAVED = 'surveydata/SURVEYSAVED';
export const DOWNLOADING_PDF = 'surveydata/DOWNLOADING_PDF';
export const DOWNLOADING_PDF_DONE = 'surveydata/DOWNLOADING_PDF_DONE';
export const SURVEY_UPDATED = 'surveydata/SURVEYUPDATED';
export const SURVEY_REMOVED = 'surveydata/SURVEYREMOVED';
export const LOADING_SURVEYS = 'surveydata/LOADINGSURVEYS';
export const SURVEYS_LOADED = 'surveydata/SURVEYSLOADED';
export const SURVEY_LOADED = 'surveydata/SURVEYLOADED';
export const CLEAR_SURVEY_BY_ID = 'surveydata/CLEAR_SURVEY_BY_ID';

const initialState = {
    isFetchingSurveyData: false,
    downloadingPdf: false,
    // surveys will be a map keyed by id (object), not an array
    surveys: null,
};

export default (state = initialState, action) => {
    switch (action.type) {
        case SURVEY_SAVED:
            return {
                ...state,
                surveys: null,
            };
        case DOWNLOADING_PDF:
            return {
                ...state,
                downloadingPdf: true,
            };
        case DOWNLOADING_PDF_DONE: {
            return {
                ...state,
                downloadingPdf: false,
            };
        }
        case LOADING_SURVEYS: {
            return {
                ...state,
                isFetchingSurveyData: true,
                surveys: null,
            };
        }
        case SURVEYS_LOADED: {
            // convert loaded array into a map keyed by id
            const list = action.surveys || [];
            const surveys = {};
            list.forEach((s) => {
                if (s && s.id != null) {
                    surveys[s.id] = s;
                }
            });

            return {
                ...state,
                surveys,
                isFetchingSurveyData: false,
            };
        }
        case SURVEY_UPDATED: {
            const oldMap = state.surveys || {};
            const updated = action.survey;
            if (!updated) return state;
            const updatedId = updated.id;
            if (updatedId == null) return state;

            // only update if the survey already exists in the surveys map
            if (!Object.prototype.hasOwnProperty.call(oldMap, updatedId)) return state;

            const newSurveys = {
                ...oldMap,
                [updatedId]: { ...oldMap[updatedId], ...updated },
            };

            return {
                ...state,
                surveys: newSurveys,
            };
        }
        case SURVEY_REMOVED: {
            const removed = action.survey;
            if (!removed) return state;
            const removedId = removed.id;
            if (removedId == null) return state;

            const newSurveys = { ...(state.surveys || {}) };
            delete newSurveys[removedId];

            return {
                ...state,
                surveys: newSurveys,
            };
        }
        case SURVEY_LOADED: {
            const loaded = action.survey;
            if (!loaded) return state;
            const loadedId = loaded.id;
            if (loadedId == null) return state;

            const oldMap = state.surveys || {};
            const newSurveys = {
                ...oldMap,
                [loadedId]: loaded,
            };

            return {
                ...state,
                surveys: newSurveys,
            };
        }
        case CLEAR_SURVEY_BY_ID: {
            if (!action.surveyId) return state;
            const old = state.surveys || {};
            const newSurveys = { ...old };
            delete newSurveys[action.surveyId];
            return { ...state, surveys: newSurveys };
        }
        case LOGGED_OFF:
            return initialState;
        default:
            return state;
    }
};

export const downloadingPdf = () => {
    return (dispatch) => {
        dispatch({ type: DOWNLOADING_PDF });
    };
};

export const downloadingPdfDone = () => {
    return (dispatch) => {
        dispatch({ type: DOWNLOADING_PDF_DONE });
    };
};

export const getOpenSurveysForUserPortfolio = () => {
    return async (dispatch) => {
        dispatch({ type: LOADING_SURVEYS });
        try {
            const resp = await Ajax.get('api/survey/all');
            dispatch({ type: SURVEYS_LOADED, surveys: resp.data });
            return resp.data;
        } catch (err) {
            return null;
        }
    };
};

export const getSurvey = (surveyId) => {
    return async (dispatch) => {
        try {
            const resp = await Ajax.get(`api/survey/${surveyId}`);
            dispatch({ type: SURVEY_LOADED, survey: resp.data });
            return resp.data;
        } catch (err) {
            console.log(err);
        }
    };
};

export const getSurveyPdf = (surveyId, permanentStore, fileName = 'document.pdf') => {
    return async (dispatch) => {
        try {
            await Ajax.post('api/survey/pdf/' + surveyId, { permanentStore: permanentStore });
            toast.info(i18next.t('createPdf.pdfSurveyCreateInProgress'), { autoClose: 10000, hideProgressBar: true });
        } catch (err) {
            console.log(err);
            dispatch({ type: DOWNLOADING_PDF_DONE });
            toast.info(i18next.t('createPdf.createPdfFail'), { autoClose: 5000, hideProgressBar: true });
        }
    };
};

export const getStoredSurveyPdf = (surveyId) => {
    return async (dispatch) => {
        try {
            const resp = await Ajax.get('api/survey/pdf/' + surveyId);
            return resp.data;
        } catch (err) {
            console.log(err);
            return null;
        }
    };
};

export const getInspectionPdf = (
    surveyId,
    inspectionReport,
    editorHtmlContent,
    machineNotes,
    selectedImages,
    svgHtmlDict,
    selectedVideos,
    permanentStore
) => {
    return async (dispatch) => {
        try {
            await Ajax.post('api/survey/inspectionPdf/' + surveyId, {
                InspectionReport: inspectionReport,
                EnrichmentBase64: editorHtmlContent,
                MachineNotes: machineNotes,
                SelectedImages: selectedImages,
                SvgGraphs: svgHtmlDict,
                SelectedVideos: selectedVideos,
                PermanentStore: permanentStore,
            });
            toast.info(i18next.t('createPdf.pdfSurveyCreateInProgress'), { autoClose: 10000, hideProgressBar: true });
        } catch (err) {
            console.log(err);
            dispatch({ type: DOWNLOADING_PDF_DONE });
            toast.info(i18next.t('createPdf.createPdfFail'), { autoClose: 5000, hideProgressBar: true });
        }
    };
};

export const getImageUrl = (imageId) => {
    return async (dispatch) => {
        try {
            const resp = await Ajax.get('api/survey/image/' + imageId);
            return resp.data;
        } catch (err) {
            return null;
        }
    };
};

export const getVideoUrl = (videoId) => {
    return async (dispatch) => {
        try {
            const resp = await Ajax.get('api/survey/video/' + videoId);
            return resp.data;
        } catch (err) {
            toast.info(i18next.t('survey.videoLoadFail'), { autoClose: 5000, hideProgressBar: true });
            return null;
        }
    };
};

export const saveSurvey = (survey, surveyEnrichment, excludedImages) => {
    return async (dispatch) => {
        try {
            const response = await Ajax.put('api/survey/', {
                Survey: survey,
                EnrichmentBase64: surveyEnrichment,
                ExcludeImageIds: excludedImages,
            });
            if (response.status === 204) {
                toast.info(i18next.t('survey.savingSuccess'), { autoClose: 1500, hideProgressBar: true });
                dispatch({ type: SURVEY_SAVED });
                return true;
            } else {
                return false;
            }
        } catch (err) {
            toast.info(i18next.t('survey.savingFail'), { autoClose: 5000, hideProgressBar: true });
            return false;
        }
    };
};

export const changeSurveyStatus = (surveyId, status) => {
    return async (dispatch) => {
        try {
            await Ajax.put(`api/survey/${surveyId}/status/${status}`);
            return true;
        } catch (err) {
            return false;
        }
    };
};

export const getSurveyEnrichment = (surveyId) => {
    return async (dispatch) => {
        try {
            const resp = await Ajax.get('api/survey/enrichment/' + surveyId);
            return resp.data;
        } catch (err) {
            return null;
        }
    };
};

export const deleteSurvey = async (surveyId) => {
    try {
        await Ajax.delete('api/survey/' + surveyId);
    } catch (err) {
        console.log(err);
    }
};

export const getSurveyExcludedImages = async (surveyId) => {
    try {
        const resp = await Ajax.get('api/survey/excludedimages/' + surveyId);
        return resp.data;
    } catch (err) {
        return null;
    }
};

export const getPhraseCategories = async () => {
    try {
        const resp = await Ajax.get('api/survey/phrases');
        return resp.data;
    } catch (err) {
        return null;
    }
};

export const surveyUpdated = (survey) => {
    return (dispatch) => {
        dispatch({
            type: SURVEY_UPDATED,
            survey,
        });
    };
};

export const surveyRemoved = (survey) => {
    return (dispatch) => {
        dispatch({
            type: SURVEY_REMOVED,
            survey,
        });
    };
};
